import type {
    FormFieldAction,
    FormFieldCondition,
    FormFieldJumpAction,
} from "~/models/FormField";
export function useFormLogic() {
    const store = useFormStateStore();

    function getLogic(): FormFieldAction[] {
        const logic = [];
        const fallback = [];

        for (const action of store.activeField?.logic ?? []) {
            if (action?.condition?.operator === "fallback") {
                fallback.push(action);
            } else {
                logic.push(action);
            }
        }

        return [...logic, ...fallback];
    }
    function getValue(id?: string): any {
        if (!id) return;
        return store.data[id];
    }

    function canTestLogic(id?: string): boolean {
        if (!id) return false;

        const field = store.form?.fields?.find((field) => id === field.id);

        if (!field) return false;
        if (
            field.required &&
            !(
                id in store.data &&
                (Array.isArray(store.data[id])
                    ? store.data[id].length
                    : store.data[id])
            )
        )
            return false;

        return true;
    }

    function test() {
        const logic = getLogic();

        if (!canTestLogic(store.activeField.id)) {
            return true;
        }

        if (!logic.length) {
            return false;
        }

        for (const action of logic) {
            switch (action.type) {
                case "jump":
                    const jump = actions.jump(action);

                    if (jump && action?.details?.to?.value) {
                        const next = store.form.fields.find(
                            (field) => field.id === action.details.to.value,
                        );

                        if (!next) return false;

                        if (next.hidden) {
                            store.next();
                        } else {
                            store.handleNext(next);
                        }

                        return true;
                    }
                    break;
            }
        }

        return false;
    }

    function validate(
        conditions: FormFieldCondition[],
        operator: "and" | "or" = "and",
    ) {
        for (const condition of conditions) {
            if (!condition.field && !condition.operator) {
                if (operator === "and") return false;
                continue;
            }

            if (!canTestLogic(condition.field)) {
                return false;
            }

            let valid = false;
            const value = getValue(condition.field);

            const isString = (val: any): boolean => typeof val === "string";
            const isNumber = (val: any): boolean =>
                typeof val === "number" && Number.isFinite(val);
            const toArray = (val: any) => (Array.isArray(val) ? val : [val]);

            let conVal: any;
            console.log({
                value,
                condition,
                operator: condition.operator,
            });
            switch (condition.operator) {
                case "has_response":
                    valid = !!value;
                    break;
                case "between":
                    const val = toArray(value ?? []);
                    conVal = toArray(condition.value ?? []).filter((conVal) =>
                        isNumber(conVal),
                    );

                    if (conVal.length !== 2) valid = false;
                    else
                        valid =
                            val.filter(
                                (subVal) =>
                                    isNumber(subVal) &&
                                    subVal >= conVal[0] &&
                                    subVal <= conVal[1],
                            ).length > 0;
                    break;
                case "equals":
                    valid = value === condition.value;

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = value[0] === condition.value;
                    }
                    break;
                case "not_equals":
                    valid = value !== condition.value;

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = value[0] !== condition.value;
                    }
                    break;
                case "contains":
                    valid =
                        isString(value) &&
                        isString(condition.value) &&
                        value.includes(condition.value);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = value[0].includes(condition.value);
                    }
                    break;
                case "does_not_contain":
                    valid =
                        isString(value) &&
                        isString(condition.value) &&
                        !value.includes(condition.value);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = !value[0].includes(condition.value);
                    }
                    break;
                case "starts_with":
                    valid =
                        isString(value) &&
                        isString(condition.value) &&
                        value.startsWith(condition.value);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = value[0].startsWith(condition.value);
                    }
                    break;
                case "ends_with":
                    valid =
                        isString(value) &&
                        isString(condition.value) &&
                        value.endsWith(condition.value);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = value[0].endsWith(condition.value);
                    }
                    break;
                case "does_not_start_with":
                    valid =
                        isString(value) &&
                        isString(condition.value) &&
                        !value.startsWith(condition.value);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = !value[0].startsWith(condition.value);
                    }
                    break;
                case "does_not_end_with":
                    valid =
                        isString(value) &&
                        isString(condition.value) &&
                        !value.endsWith(condition.value);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = !value[0].endsWith(condition.value);
                    }
                    break;
                case "greater_than":
                    valid =
                        isNumber(value) &&
                        isNumber(condition.value) &&
                        value > (condition.value as number);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = isNumber(value[0]) && isNumber(condition.value) && value[0] > (condition.value as number);
                    }
                    break;
                case "less_than":
                    valid =
                        isNumber(value) &&
                        isNumber(condition.value) &&
                        value < (condition.value as number);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = isNumber(value[0]) && isNumber(condition.value) && value[0] < (condition.value as number);
                    }
                    break;
                case "greater_than_or_equal":
                    valid =
                        isNumber(value) &&
                        isNumber(condition.value) &&
                        value >= (condition.value as number);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = isNumber(value[0]) && isNumber(condition.value) && value[0] >= (condition.value as number);
                    }
                    break;
                case "less_than_or_equal":
                    valid =
                        isNumber(value) &&
                        isNumber(condition.value) &&
                        value <= (condition.value as number);

                    if (!valid && Array.isArray(value) && value.length === 1) {
                        valid = isNumber(value[0]) && isNumber(condition.value) && value[0] <= (condition.value as number);
                    }
                    break;
                case "one_of":
                    conVal = toArray(condition.value ?? []);

                    valid =
                        toArray(value ?? []).filter((val) =>
                            conVal.includes(val),
                        ).length > 0;
                    break;
                case "not_one_of":
                    conVal = toArray(condition.value ?? []);

                    valid =
                        toArray(value ?? []).filter((val) =>
                            conVal.includes(val),
                        ).length === 0;
                    break;
            }

            if (valid && operator === "or") return true;
            if (!valid && operator === "and") return false;
        }

        return true;
    }

    const actions = {
        jump: function (action: FormFieldJumpAction) {
            switch (action.condition.operator) {
                case "if":
                    if (validate(action.condition.conditions)) return true;
                    break;
                case "always":
                case "fallback":
                    return true;
            }

            return false;
        },
    };

    return {
        test,
    };
}
